import React, { useEffect, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
    getGrupos, getValores, crear, actualizar, desactivar, guardarParametro, cargaMasiva, logMaestro,
    GRUPO_PARAMETROS, GRUPO_BARCO, GRUPO_PRIORIDAD_VACIO
} from '../../services/Maestros/MaestroDoleService';

/**
 * Pantalla exclusiva de Maestros y Parámetros de DOLE (RF-C5).
 *
 * El usuario ve pestañas amigables (Barcos, Zonas, Categorías, Prioridades,
 * Parámetros); por debajo todo vive en `configuracion_det` bajo los grupos
 * DOL-0000-*. Es la MISMA fuente contra la que se valida la carga masiva
 * (RF-B10): lo que se crea aquí queda disponible para el próximo archivo.
 *
 * Prioridad de Vacío: su "parámetro" es la hora de Cut-Off (B6). Es la fuente
 * única: el Estado de Posicionamiento la lee en vivo desde la prioridad (no se
 * copia al booking). Cada alta/edición/baja queda en el log (RF-LOG), y se puede
 * cargar masivamente por archivo (Nombre + Código).
 */
const MaestrosDole = () => {

    const toast = useRef(null);
    const fileRef = useRef(null);
    const [grupos, setGrupos] = useState([]);
    const [indice, setIndice] = useState(0);
    const [valores, setValores] = useState([]);
    const [bloqueado, setBloqueado] = useState(false);

    const [dialogoVisible, setDialogoVisible] = useState(false);
    const [editando, setEditando] = useState(null);   // null = alta
    const [nombre, setNombre] = useState('');
    const [parametro, setParametro] = useState('');

    const [resultadoCarga, setResultadoCarga] = useState(null);
    const [logVisible, setLogVisible] = useState(false);
    const [logFila, setLogFila] = useState(null);
    const [logDatos, setLogDatos] = useState([]);

    const grupoActual = grupos[indice];
    const esParametros = grupoActual && grupoActual.codigo === GRUPO_PARAMETROS;
    const esBarcos = grupoActual && grupoActual.codigo === GRUPO_BARCO;
    const esPrioridad = grupoActual && grupoActual.codigo === GRUPO_PRIORIDAD_VACIO;

    // Etiqueta de la columna auxiliar (parametro) según el maestro.
    const etiquetaParam = esParametros ? 'Valor' : (esBarcos ? 'Sigla' : (esPrioridad ? 'Cut-Off (hora)' : 'Parámetro'));

    const error = (e) => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message || e, life: 6000 });
    const ok = (msg) => toast.current.show({ severity: 'success', summary: 'Listo', detail: msg, life: 3000 });

    useEffect(() => {
        getGrupos().then(setGrupos).catch(error);
    }, []);

    useEffect(() => {
        if (!grupoActual) return;
        setBloqueado(true);
        getValores(grupoActual.codigo)
            .then(setValores)
            .catch(error)
            .finally(() => setBloqueado(false));
    }, [grupoActual]); // eslint-disable-line react-hooks/exhaustive-deps

    const recargar = () => {
        if (!grupoActual) return;
        getValores(grupoActual.codigo).then(setValores).catch(error);
    };

    const abrirAlta = () => {
        setEditando(null);
        setNombre('');
        setParametro('');
        setDialogoVisible(true);
    };

    const abrirEdicion = (fila) => {
        setEditando(fila);
        setNombre(fila.nombre || '');
        setParametro(fila.parametro || '');
        setDialogoVisible(true);
    };

    const guardar = async () => {
        if (!nombre.trim()) {
            error('El nombre es obligatorio');
            return;
        }
        if (esPrioridad && parametro.trim() && !/^\d{1,2}:\d{2}$/.test(parametro.trim())) {
            error('El Cut-Off debe tener formato HH:mm (ej. 14:00)');
            return;
        }
        setBloqueado(true);
        try {
            if (esParametros && editando) {
                // en Parámetros lo que se edita es el VALOR
                await guardarParametro(editando.nombre, parametro);
            } else if (editando) {
                await actualizar(editando.configuracionDetId, nombre, parametro);
            } else {
                await crear(grupoActual.codigo, nombre, parametro);
            }
            ok('Guardado');
            setDialogoVisible(false);
            recargar();
        } catch (e) {
            error(e);
        } finally {
            setBloqueado(false);
        }
    };

    const darDeBaja = async (fila) => {
        setBloqueado(true);
        try {
            await desactivar(fila.configuracionDetId);
            ok('Dado de baja');
            recargar();
        } catch (e) {
            error(e);
        } finally {
            setBloqueado(false);
        }
    };

    // ---- carga masiva ----
    const elegirArchivo = () => {
        if (fileRef.current) {
            fileRef.current.value = '';
            fileRef.current.click();
        }
    };

    const subirArchivo = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !grupoActual) return;
        setBloqueado(true);
        try {
            const r = await cargaMasiva(grupoActual.codigo, file);
            if (r.error) {
                error(r.error);
            } else {
                setResultadoCarga(r);
                ok(`Carga: ${r.creados} creados, ${r.actualizados} actualizados` +
                    (r.erroresCount ? `, ${r.erroresCount} con novedad` : ''));
                recargar();
            }
        } catch (err) {
            error(err);
        } finally {
            setBloqueado(false);
        }
    };

    // ---- log ----
    const verLog = async (fila) => {
        setLogFila(fila);
        setLogVisible(true);
        setLogDatos([]);
        try {
            setLogDatos(await logMaestro(fila.configuracionDetId));
        } catch (e) {
            error(e);
        }
    };

    const accionesTemplate = (fila) => (
        <div className="flex gap-1">
            <Button icon="pi pi-pencil" className="p-button-sm p-button-text" tooltip="Editar"
                onClick={() => abrirEdicion(fila)} />
            {!esParametros && (
                <Button icon="pi pi-history" className="p-button-sm p-button-text" tooltip="Ver historial"
                    onClick={() => verLog(fila)} />
            )}
            {!esParametros && (
                <Button icon="pi pi-ban" className="p-button-sm p-button-text p-button-danger" tooltip="Dar de baja"
                    onClick={() => darDeBaja(fila)} />
            )}
        </div>
    );

    const fechaTemplate = (l) => (l.fecha ? new Date(l.fecha).toLocaleString() : '');
    const cambioTemplate = (l) => {
        if (l.valorAnterior == null && l.valorNuevo != null && !['ALTA', 'BAJA'].includes(l.campo)) {
            return `— → ${l.valorNuevo}`;
        }
        if (['ALTA', 'BAJA'].includes(l.campo)) return l.campo;
        return `${l.valorAnterior ?? '—'} → ${l.valorNuevo ?? '—'}`;
    };

    return (
        <>
            <Toast ref={toast} />
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={subirArchivo} />
            <BlockUI blocked={bloqueado} template={<ProgressSpinner style={{ width: '50px', height: '50px' }} />}>
                <div className="card">
                    <h5>Maestros DOLE</h5>
                    <p className="mt-0">
                        Estos son los valores contra los que se valida el archivo de carga. Lo que se crea aquí
                        queda disponible para la próxima carga.
                    </p>

                    <TabView activeIndex={indice} onTabChange={(e) => setIndice(e.index)}>
                        {grupos.map((g) => (
                            <TabPanel key={g.codigo} header={g.etiqueta}>
                                <div className="flex justify-content-end gap-2 mb-2">
                                    {/* En Parámetros no se dan de alta valores nuevos: son fijos */}
                                    {!esParametros && (
                                        <>
                                            <Button label="Subir archivo" icon="pi pi-upload"
                                                className="p-button-outlined" onClick={elegirArchivo} />
                                            <Button label="Nuevo" icon="pi pi-plus" onClick={abrirAlta} />
                                        </>
                                    )}
                                </div>
                                <DataTable value={valores} size="small" paginator rows={15}
                                    emptyMessage="Sin valores cargados">
                                    <Column field="nombre" header={esParametros ? 'Parámetro' : 'Nombre'}
                                        style={{ minWidth: '16rem' }} />
                                    <Column field="parametro" header={etiquetaParam}
                                        style={{ minWidth: '10rem' }} />
                                    <Column field="estado" header="Estado" style={{ minWidth: '8rem' }} />
                                    <Column body={accionesTemplate} header="Acciones" style={{ minWidth: '10rem' }} />
                                </DataTable>
                            </TabPanel>
                        ))}
                    </TabView>
                </div>
            </BlockUI>

            <Dialog header={editando ? 'Editar' : 'Nuevo'} visible={dialogoVisible} style={{ width: '460px' }} modal
                onHide={() => setDialogoVisible(false)}
                footer={
                    <div>
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text"
                            onClick={() => setDialogoVisible(false)} />
                        <Button label="Guardar" icon="pi pi-check" onClick={guardar} />
                    </div>
                }>
                <div className="grid p-fluid">
                    <div className="col-12">
                        <label htmlFor="nombre">{esParametros ? 'Parámetro' : 'Nombre'}</label>
                        <InputText id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)}
                            disabled={esParametros} />
                    </div>
                    <div className="col-12">
                        <label htmlFor="parametro">
                            {esParametros ? 'Valor'
                                : (esBarcos ? 'Sigla (ej. DPC)'
                                    : (esPrioridad ? 'Cut-Off (hora HH:mm)' : 'Parámetro (opcional)'))}
                        </label>
                        <InputText id="parametro" value={parametro} onChange={(e) => setParametro(e.target.value)}
                            placeholder={esPrioridad ? 'ej. 14:00' : ''} />
                        {esBarcos && <small>La sigla es lo que trae la columna BARCO-VIAJE del archivo (ej. DPC609 → DPC).</small>}
                        {esPrioridad && <small>Hora límite del vacío; alimenta el Estado de Posicionamiento (B6).</small>}
                    </div>
                </div>
            </Dialog>

            {/* Resultado de la carga masiva */}
            <Dialog header="Resultado de la carga" visible={!!resultadoCarga} style={{ width: '600px' }} modal
                onHide={() => setResultadoCarga(null)}>
                {resultadoCarga && (
                    <div>
                        <p>
                            <b>{resultadoCarga.creados}</b> creados, <b>{resultadoCarga.actualizados}</b> actualizados
                            {resultadoCarga.erroresCount ? <>, <b>{resultadoCarga.erroresCount}</b> con novedad</> : null}.
                        </p>
                        {resultadoCarga.errores && resultadoCarga.errores.length > 0 && (
                            <DataTable value={resultadoCarga.errores} size="small" paginator rows={10}>
                                <Column field="linea" header="Línea" style={{ width: '6rem' }} />
                                <Column field="nombre" header="Nombre" />
                                <Column field="motivo" header="Novedad" />
                            </DataTable>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Historial de un valor */}
            <Dialog header={logFila ? `Historial — ${logFila.nombre}` : 'Historial'} visible={logVisible}
                style={{ width: '760px' }} modal onHide={() => setLogVisible(false)}>
                <DataTable value={logDatos} size="small" paginator rows={10} emptyMessage="Sin movimientos">
                    <Column body={fechaTemplate} header="Fecha" style={{ minWidth: '12rem' }} />
                    <Column field="campo" header="Campo" style={{ minWidth: '8rem' }} />
                    <Column body={cambioTemplate} header="Cambio" style={{ minWidth: '14rem' }} />
                    <Column field="motivo" header="Motivo" style={{ minWidth: '12rem' }} />
                </DataTable>
            </Dialog>
        </>
    );
};

export default MaestrosDole;
